import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Modal,
  Typography,
  TextField,
  Button,
  IconButton,
  Avatar,
  Chip,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  ListSubheader,
  LinearProgress,
  Tooltip,
  FormHelperText,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { BUTTON_STYLES, MODAL_STYLES } from "../../styles/componentStyles";
import api from "../../axiosInstance";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const BRAND = "#17479e";
const PROFILE_IMAGE_URL = "/api/ftp/mentorProfileImageById";
const DEMO_VIDEO_URL = "/api/ftp/uploadMentorDemoVideoById";
const DELETE_URL = "/api/ftp/delete";

const resolveMediaUrl = (path) =>
  !path
    ? ""
    : /^https?:\/\//.test(path)
      ? path
      : `https://siaedutech.com/public${path.startsWith("/") ? "" : "/"}${path}`;

const validationSchema = Yup.object({
  fullName: Yup.string().trim().required("Full name is required"),
  email: Yup.string().trim().email("Enter a valid email").required("Email is required"),
  phoneNumber: Yup.string().trim().required("Phone number is required"),
  highestQualification: Yup.string().trim().required("Qualification is required"),
  experienceYears: Yup.string().trim().required("Experience is required"),
  bio: Yup.string().trim(),
});

// Small inline form for adding one demo video, seeded from the same
// group-subject list used for "Subjects Taught".
function AddDemoVideoRow({ groupSubjects, mentorId, onAdd, onCancel }) {
  const videoInputRef = useRef(null);
  const [groupSubjectId, setGroupSubjectId] = useState("");
  const [title, setTitle] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [videoPath, setVideoPath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError("");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setProgress(0);
    setError("");
    const formData = new FormData();
    formData.append("file", selectedFile);
    try {
      const res = await api.post(DEMO_VIDEO_URL, formData, {
        params: { userId: mentorId },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      setVideoPath(res.data?.data?.path);
      setProgress(100);
      toast.success("Demo video uploaded.");
    } catch (err) {
      const message =
        err.response?.data?.errorMessage || "Upload failed. Please try again.";
      setError(message);
      toast.error(message);
      setProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleAdd = () => {
    if (!groupSubjectId) return setError("Please select a subject.");
    if (!title.trim()) return setError("Please enter a demo title.");
    if (!videoPath) return setError("Please upload the demo video.");
    const subject = groupSubjects.find(
      (s) => String(s.id) === String(groupSubjectId),
    );
    onAdd({
      groupSubjectId,
      subjectName: subject?.groupSubjectTitle || "",
      title: title.trim(),
      videoPath,
    });
  };

  return (
    <Box
      sx={{
        border: "1px dashed #c3ccdb",
        borderRadius: 2,
        p: 1.5,
        mb: 1.5,
        bgcolor: "#f6f8fc",
      }}
    >
      <FormControl fullWidth size="small" sx={{ mb: 1.2 }}>
        <InputLabel>Subject</InputLabel>
        <Select
          label="Subject"
          value={groupSubjectId}
          onChange={(e) => setGroupSubjectId(e.target.value)}
        >
          {groupSubjects.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.groupSubjectTitle}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        fullWidth
        size="small"
        label="Demo Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        sx={{ mb: 1.2 }}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        hidden
        onChange={handleFileChange}
      />
      {!videoPath ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => videoInputRef.current?.click()}
            sx={{ textTransform: "none" }}
          >
            Choose Video
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }} noWrap>
            {selectedFile ? selectedFile.name : "No video chosen"}
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            sx={{ textTransform: "none", bgcolor: BRAND }}
          >
            {uploading ? "Uploading…" : "Upload"}
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PlayCircleFilledIcon sx={{ color: "#2e7d32" }} />
          <Typography variant="body2" sx={{ color: "#2e7d32", fontWeight: 600 }}>
            Video uploaded
          </Typography>
        </Box>
      )}
      {uploading && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress variant="determinate" value={progress} />
        </Box>
      )}
      {error && (
        <Typography sx={{ color: "error.main", fontSize: 12, mt: 1 }}>
          {error}
        </Typography>
      )}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.2 }}>
        <Button size="small" onClick={onCancel} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleAdd}
          disabled={!videoPath}
          sx={{ textTransform: "none", bgcolor: BRAND }}
        >
          Add Demo
        </Button>
      </Box>
    </Box>
  );
}

function MentorRegisterForm({ open, onClose, mentorEdit, onSuccess }) {
  const profile = mentorEdit?.profile || {};
  const mentorProfileId = profile.id;
  const mentorId = profile.mentorId;

  const [universities, setUniversities] = useState([]);
  const [courses, setCourses] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [coreSubjects, setCoreSubjects] = useState([]);
  const [groupSubjects, setGroupSubjects] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [demoVideos, setDemoVideos] = useState([]);
  const [addingDemo, setAddingDemo] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deletingImage, setDeletingImage] = useState(false);

  // Load the master lists once the popup opens.
  useEffect(() => {
    if (!open) return;
    setLoadingLists(true);
    Promise.all([
      api.get("/getCourses", { params: { active: true } }).then((res) => setCourses(res.data?.data || [])),
      api
        .get("/getAllUniversities")
        .then((res) => setUniversities(res.data?.data || [])),
      api
        .get("/mentor-catalog/languages")
        .then((res) => setLanguages(res.data?.data || [])),
      api
        .get("/mentor-catalog/qualifications")
        .then((res) => setQualifications(res.data?.data || [])),
      api
        .get("/mentor-catalog/coreSubjects")
        .then((res) => setCoreSubjects(res.data?.data || [])),
    ])
      .catch(() => toast.error("Failed to load some reference lists."))
      .finally(() => setLoadingLists(false));
  }, [open]);

  // Seed the existing demo videos (as recorded on the mentor row) once opened.
  // The mentor row already carries groupSubjectId directly on each video.
  useEffect(() => {
    if (!open) return;
    setDemoVideos(
      (mentorEdit?.demoVideos || []).map((v) => ({
        groupSubjectId: v.groupSubjectId,
        subjectName: v.subjectName,
        title: v.title || "",
        videoPath: v.videoUrl,
      })),
    );
  }, [open, mentorEdit]);

  const formik = useFormik({
    initialValues: {
      fullName: "",
      email: "",
      phoneNumber: "",
      highestQualification: "",
      experienceYears: "",
      bio: "",
      profileImageUrl: "",
      universitiesIds: [],
      courseIds: [],
      languageIds: [],
      coreSubjectIds: [],
      groupSubjectIds: [],
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!mentorProfileId) {
        toast.error("Missing mentor profile id.");
        return;
      }
      try {
        setSubmitting(true);
        await api.put(`/admin/mentors/${mentorProfileId}/profile`, {
          registration: {
            fullName: values.fullName.trim(),
            email: values.email.trim(),
            phoneNumber: values.phoneNumber.trim(),
            highestQualification: values.highestQualification.trim(),
            experienceYears: values.experienceYears,
            bio: values.bio.trim(),
            profileImageUrl: values.profileImageUrl,
            courseIds: values.courseIds,
            languageIds: values.languageIds,
            groupSubjectIds: values.groupSubjectIds,
            universitiesIds: values.universitiesIds,
            demoVideos: demoVideos.map((d) => ({
              groupSubjectId: Number(d.groupSubjectId) || d.groupSubjectId,
              title: d.title,
              videoUrl: d.videoPath,
            })),
          },
        });
        toast.success("Mentor profile updated successfully!");
        onSuccess?.();
        onClose();
      } catch (error) {
        toast.error(
          error.response?.data?.errorMessage ||
            "Failed to update mentor profile. Please try again.",
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Prefill everything from the row being edited. The mentor row carries
  // real ids directly (universityId, courseId, languageId, groupSubjectId),
  // so selections can be seeded straight from it — no name-matching needed.
  useEffect(() => {
    if (!open) return;
    const courseIds = Array.from(
      new Set(
        (mentorEdit?.subjects || [])
          .flatMap((s) => s.courses || [])
          .map((c) => c.courseId),
      ),
    );
    formik.setValues((prev) => ({
      ...prev,
      fullName: profile.username || "",
      email: profile.email || "",
      phoneNumber: profile.phoneNumber || "",
      highestQualification: profile.highestQualification || "",
      experienceYears:
        profile.experienceYears != null ? String(profile.experienceYears) : "",
      bio: profile.bio || "",
      profileImageUrl:
        profile.profileImageUrl || profile.profileImage || "",
      universitiesIds: (mentorEdit?.universities || []).map(
        (u) => u.universityId,
      ),
      courseIds,
      languageIds: (mentorEdit?.languages || []).map((l) => l.languageId),
      groupSubjectIds: (mentorEdit?.subjects || []).map(
        (s) => s.groupSubjectId,
      ),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mentorEdit]);

  // Once Core Subjects are picked, load the Subjects Taught options for
  // exactly those categories in a single call.
  const coreSubjectIdsKey = formik.values.coreSubjectIds.join(",");
  useEffect(() => {
    if (!open) return;
    if (!coreSubjectIdsKey) {
      setGroupSubjects([]);
      return;
    }
    setLoadingSubjects(true);
    api
      .get("/mentor-catalog/groupedSubjects", {
        params: { coreSubjects: coreSubjectIdsKey },
      })
      .then((res) => {
        const items = res.data?.data || [];
        setGroupSubjects(
          items.map((g) => {
            const catId =
              g.coreSubjectId ?? g.core_subject_id ?? g.subjectCategoryId;
            const cat = coreSubjects.find((cs) => cs.id === catId);
            return {
              ...g,
              coreSubjectId: catId ?? null,
              coreSubjectName: cat?.subjectName || "Other",
            };
          }),
        );
      })
      .catch(() => {
        toast.error("Failed to load subjects for the selected core subjects.");
        setGroupSubjects([]);
      })
      .finally(() => setLoadingSubjects(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, coreSubjectIdsKey]);

  const imageInputRef = useRef(null);
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !mentorId) return;
    setUploadingImage(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post(PROFILE_IMAGE_URL, formData, {
        params: { userId: mentorId },
      });
      formik.setFieldValue("profileImageUrl", res.data?.data?.path || "");
      toast.success("Profile photo uploaded.");
    } catch (err) {
      toast.error(
        err.response?.data?.errorMessage || "Failed to upload photo.",
      );
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const handleDeleteImage = async () => {
    if (!formik.values.profileImageUrl) return;
    setDeletingImage(true);
    try {
      await api.put(DELETE_URL, { path: formik.values.profileImageUrl });
      formik.setFieldValue("profileImageUrl", "");
      toast.success("Profile photo removed.");
    } catch (err) {
      toast.error(
        err.response?.data?.errorMessage || "Failed to remove photo.",
      );
    } finally {
      setDeletingImage(false);
    }
  };

  const handleAddDemo = (demo) => {
    setDemoVideos((prev) => [...prev, demo]);
    setAddingDemo(false);
  };

  const handleDeleteDemo = async (demo) => {
    try {
      if (demo.videoPath) await api.put(DELETE_URL, { path: demo.videoPath });
    } catch {
      // best-effort cleanup only — the demo is still removed from the list below
    }
    setDemoVideos((prev) => prev.filter((d) => d !== demo));
    toast.success("Demo video removed.");
  };

  // Courses carry a universityId, so narrow the Courses list once at least
  // one university is picked (falls back to the full list if that field
  // isn't present on this data, or nothing is selected yet).
  const coursesHaveUniversity = courses.some((c) => c.universityId != null);
  const filteredCourses =
    coursesHaveUniversity && formik.values.universitiesIds.length
      ? courses.filter((c) =>
          formik.values.universitiesIds.includes(c.universityId),
        )
      : courses;

  // Labels for already-selected subjects fall back to the mentor's own row
  // data, so existing selections still show a name before a Core Subject
  // filter has been picked (and therefore before groupSubjects is loaded).
  const mentorSubjectNameById = new Map(
    (mentorEdit?.subjects || []).map((s) => [s.groupSubjectId, s.subjectName]),
  );
  const resolveSubjectTitle = (id) =>
    groupSubjects.find((s) => s.id === id)?.groupSubjectTitle ||
    mentorSubjectNameById.get(id) ||
    null;

  // Group subjects by core subject for the Select's grouped menu.
  const groupedSubjectMenu = [];
  const seenCategories = new Set();
  groupSubjects.forEach((s) => {
    if (!seenCategories.has(s.coreSubjectName)) {
      seenCategories.add(s.coreSubjectName);
      groupedSubjectMenu.push({ header: s.coreSubjectName });
    }
    groupedSubjectMenu.push({ item: s });
  });

  return (
    <Modal open={open} onClose={onClose} disableScrollLock>
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        sx={{
          ...MODAL_STYLES.standard,
          width: { xs: "94%", sm: 520, md: 620 },
          maxWidth: "94vw",
          maxHeight: "92vh",
          p: 0,
          borderRadius: 3,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            background: `linear-gradient(135deg, ${BRAND}, #2a63c0)`,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
              Edit Mentor Profile
            </Typography>
            {(profile.username || mentorProfileId) && (
              <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
                {profile.username}
                {profile.username && mentorProfileId ? " • " : ""}
                {mentorProfileId ? `ID ${mentorProfileId}` : ""}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
          {/* Profile photo */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Avatar
              src={resolveMediaUrl(
                formik.values.profileImageUrl ||
                  profile.profileImageUrl ||
                  profile.profileImage,
              )}
              sx={{ width: 64, height: 64, bgcolor: "#e5edfb", color: BRAND }}
            >
              <PersonIcon />
            </Avatar>
            <Box>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageChange}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploadingImage || deletingImage}
                  sx={{ textTransform: "none" }}
                >
                  {uploadingImage ? "Uploading…" : "Change Photo"}
                </Button>
                {formik.values.profileImageUrl && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteImage}
                    disabled={uploadingImage || deletingImage}
                    sx={{ textTransform: "none" }}
                  >
                    {deletingImage ? "Removing…" : "Remove"}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ fontWeight: 600, letterSpacing: 0.6 }}
          >
            Basic Info
          </Typography>
          <Stack spacing={2} sx={{ mt: 1, mb: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Full Name"
              name="fullName"
              value={formik.values.fullName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.fullName && Boolean(formik.errors.fullName)}
              helperText={formik.touched.fullName && formik.errors.fullName}
            />
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={Boolean(profile.emailVerified)}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={
                  (formik.touched.email && formik.errors.email) ||
                  (profile.emailVerified
                    ? "Email is verified and cannot be changed"
                    : "")
                }
                InputProps={{
                  endAdornment: (
                    <Tooltip
                      title={profile.emailVerified ? "Verified" : "Unverified"}
                      arrow
                    >
                      {profile.emailVerified ? (
                        <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 20 }} />
                      ) : (
                        <ErrorOutlineIcon sx={{ color: "#e65100", fontSize: 20 }} />
                      )}
                    </Tooltip>
                  ),
                }}
                sx={{ flex: 1, minWidth: 200 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Phone Number"
                name="phoneNumber"
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)
                }
                helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                sx={{ flex: 1, minWidth: 200 }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <FormControl
                fullWidth
                size="small"
                error={
                  formik.touched.highestQualification &&
                  Boolean(formik.errors.highestQualification)
                }
                sx={{ flex: 1, minWidth: 200 }}
              >
                <InputLabel>Highest Qualification</InputLabel>
                <Select
                  label="Highest Qualification"
                  name="highestQualification"
                  value={formik.values.highestQualification}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  {qualifications.map((q) => (
                    <MenuItem key={q.id} value={q.name}>
                      {q.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.highestQualification &&
                  formik.errors.highestQualification && (
                    <FormHelperText>
                      {formik.errors.highestQualification}
                    </FormHelperText>
                  )}
              </FormControl>
              <TextField
                fullWidth
                size="small"
                label="Experience (Years)"
                name="experienceYears"
                value={formik.values.experienceYears}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.experienceYears &&
                  Boolean(formik.errors.experienceYears)
                }
                helperText={
                  formik.touched.experienceYears && formik.errors.experienceYears
                }
                sx={{ flex: 1, minWidth: 200 }}
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              label="Bio"
              name="bio"
              value={formik.values.bio}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              multiline
              minRows={3}
            />
          </Stack>

          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ fontWeight: 600, letterSpacing: 0.6 }}
          >
            Universities, Courses &amp; Languages
          </Typography>
          <Stack spacing={2} sx={{ mt: 1, mb: 3 }}>
            <FormControl fullWidth size="small" disabled={loadingLists}>
              <InputLabel>Universities</InputLabel>
              <Select
                multiple
                value={formik.values.universitiesIds}
                onChange={(e) =>
                  formik.setFieldValue("universitiesIds", e.target.value)
                }
                input={<OutlinedInput label="Universities" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => {
                      const name = universities.find((u) => u.id === id)?.name;
                      return name ? (
                        <Chip key={id} label={name} size="small" />
                      ) : null;
                    })}
                  </Box>
                )}
              >
                {universities.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    <Checkbox
                      checked={formik.values.universitiesIds.includes(u.id)}
                    />
                    <ListItemText primary={u.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={loadingLists}>
              <InputLabel>Courses</InputLabel>
              <Select
                multiple
                value={formik.values.courseIds}
                onChange={(e) => formik.setFieldValue("courseIds", e.target.value)}
                input={<OutlinedInput label="Courses" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => {
                      const name = courses.find((c) => c.id === id)?.courseName;
                      return name ? (
                        <Chip key={id} label={name} size="small" />
                      ) : null;
                    })}
                  </Box>
                )}
              >
                {filteredCourses.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    <Checkbox checked={formik.values.courseIds.includes(c.id)} />
                    <ListItemText primary={c.courseName} />
                  </MenuItem>
                ))}
              </Select>
              {coursesHaveUniversity && formik.values.universitiesIds.length > 0 && (
                <FormHelperText>
                  Showing courses for the selected universities only
                </FormHelperText>
              )}
            </FormControl>

            <FormControl fullWidth size="small" disabled={loadingLists}>
              <InputLabel>Languages</InputLabel>
              <Select
                multiple
                value={formik.values.languageIds}
                onChange={(e) =>
                  formik.setFieldValue("languageIds", e.target.value)
                }
                input={<OutlinedInput label="Languages" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => {
                      const name = languages.find((l) => l.id === id)?.name;
                      return name ? (
                        <Chip key={id} label={name} size="small" />
                      ) : null;
                    })}
                  </Box>
                )}
              >
                {languages.map((l) => (
                  <MenuItem key={l.id} value={l.id}>
                    <Checkbox checked={formik.values.languageIds.includes(l.id)} />
                    <ListItemText primary={l.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={loadingLists}>
              <InputLabel>Core Subjects</InputLabel>
              <Select
                multiple
                value={formik.values.coreSubjectIds}
                onChange={(e) =>
                  formik.setFieldValue("coreSubjectIds", e.target.value)
                }
                input={<OutlinedInput label="Core Subjects" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => {
                      const name = coreSubjects.find(
                        (cs) => cs.id === id,
                      )?.subjectName;
                      return name ? (
                        <Chip key={id} label={name} size="small" />
                      ) : null;
                    })}
                  </Box>
                )}
              >
                {coreSubjects.map((cs) => (
                  <MenuItem key={cs.id} value={cs.id}>
                    <Checkbox
                      checked={formik.values.coreSubjectIds.includes(cs.id)}
                    />
                    <ListItemText primary={cs.subjectName} />
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Pick core subjects to load Subjects Taught options
              </FormHelperText>
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              disabled={loadingLists || loadingSubjects}
            >
              <InputLabel>Subjects Taught</InputLabel>
              <Select
                multiple
                value={formik.values.groupSubjectIds}
                onChange={(e) =>
                  formik.setFieldValue("groupSubjectIds", e.target.value)
                }
                input={<OutlinedInput label="Subjects Taught" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => {
                      const title = resolveSubjectTitle(id);
                      return title ? (
                        <Chip key={id} label={title} size="small" />
                      ) : null;
                    })}
                  </Box>
                )}
              >
                {groupedSubjectMenu.map((entry, i) =>
                  entry.header ? (
                    <ListSubheader key={`h-${entry.header}-${i}`}>
                      {entry.header}
                    </ListSubheader>
                  ) : (
                    <MenuItem key={entry.item.id} value={entry.item.id}>
                      <Checkbox
                        checked={formik.values.groupSubjectIds.includes(
                          entry.item.id,
                        )}
                      />
                      <ListItemText primary={entry.item.groupSubjectTitle} />
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </Stack>

          {/* Demo Videos */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 600, letterSpacing: 0.6 }}
            >
              Demo Videos
            </Typography>
            {!addingDemo && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setAddingDemo(true)}
                sx={{ textTransform: "none" }}
              >
                Add Demo
              </Button>
            )}
          </Box>

          {addingDemo && (
            <AddDemoVideoRow
              groupSubjects={groupSubjects}
              mentorId={mentorId}
              onAdd={handleAddDemo}
              onCancel={() => setAddingDemo(false)}
            />
          )}

          {demoVideos.length === 0 ? (
            <Box
              sx={{
                border: "1px dashed #c3ccdb",
                borderRadius: 2,
                py: 3,
                textAlign: "center",
                color: "text.secondary",
              }}
            >
              <OndemandVideoIcon sx={{ fontSize: 30, opacity: 0.4 }} />
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                No demo videos added yet
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {demoVideos.map((demo, i) => (
                <Box
                  key={`${demo.videoPath}-${i}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Avatar
                    variant="rounded"
                    sx={{ bgcolor: "#e5edfb", color: BRAND, width: 40, height: 40 }}
                  >
                    <OndemandVideoIcon fontSize="small" />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                      {demo.subjectName || "—"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ display: "block" }}
                    >
                      {demo.title}
                    </Typography>
                  </Box>
                  <Tooltip title="Watch" arrow placement="top">
                    <IconButton
                      size="small"
                      sx={{ color: BRAND }}
                      onClick={() =>
                        window.open(resolveMediaUrl(demo.videoPath), "_blank")
                      }
                    >
                      <PlayCircleFilledIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete" arrow placement="top">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteDemo(demo)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        <Divider />
        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#fafbfd",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button onClick={onClose} {...BUTTON_STYLES.secondary}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={formik.isSubmitting}
            {...BUTTON_STYLES.primary}
          >
            {formik.isSubmitting ? "Saving…" : "Save Profile"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default MentorRegisterForm;
